import React, { useEffect, useMemo, useState } from "react";
import { AppFrame, TopBar } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Send, Trash2, Plus, Copy, MessageCircle, ChevronRight, X, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Broadcast() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [tag, setTag] = useState("");
  const [selected, setSelected] = useState([]); // product ids
  const [message, setMessage] = useState("");
  const [intro, setIntro] = useState("");
  const [step, setStep] = useState("customers"); // customers | select | send
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/customers"),
      api.get("/products", { params: { limit: 500 } }),
    ]);
    setCustomers(c.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("Name & WhatsApp number required"); return; }
    try {
      await api.post("/customers", { name: name.trim(), whatsapp_number: phone.trim(), tag: tag.trim() || null });
      toast.success("Customer added");
      setName(""); setPhone(""); setTag("");
      load();
    } catch { toast.error("Add failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this customer?")) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const toggleProduct = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const buildMessage = async () => {
    if (selected.length === 0) { toast.error("Pick at least one product"); return; }
    const { data } = await api.post("/broadcast/preview", { product_ids: selected, intro: intro || null });
    setMessage(data.message);
    setStep("send");
  };

  const copyMsg = async () => {
    await navigator.clipboard.writeText(message);
    toast.success("Message copied");
  };

  const waLink = (num) => {
    const clean = (num || "").replace(/[^0-9]/g, "");
    return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  };

  const selectedProducts = useMemo(
    () => products.filter((p) => selected.includes(p.id)),
    [selected, products],
  );

  return (
    <AppFrame>
      <TopBar title="Broadcast" subtitle="One tap → many customers" />
      <div className="px-5 mt-4">
        {/* Stepper */}
        <div className="flex gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
          <StepDot active={step === "customers"} label="1 · List" onClick={() => setStep("customers")} testid="step-customers" />
          <StepDot active={step === "select"} label="2 · Pick items" onClick={() => setStep("select")} testid="step-select" />
          <StepDot active={step === "send"} label="3 · Send" onClick={() => setStep("send")} testid="step-send" />
        </div>
      </div>

      {step === "customers" && (
        <div className="px-5 mt-5 space-y-3">
          <div className="rounded-2xl border border-black/5 bg-white p-3 space-y-2">
            <input
              data-testid="cust-name"
              value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name"
              className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
            />
            <input
              data-testid="cust-phone"
              value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210"
              className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
            />
            <input
              data-testid="cust-tag"
              value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Tag (optional) — e.g. VIP, Loves gold"
              className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
            />
            <button
              data-testid="cust-add-btn"
              onClick={add}
              className="w-full h-11 rounded-full bg-neutral-900 text-white text-sm font-medium press flex items-center justify-center gap-2"
            >
              <Plus size={14} /> Add customer
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {customers.length === 0 ? (
              <div className="rounded-2xl bg-zinc-50 border border-black/5 py-10 text-center">
                <div className="font-display text-lg">No customers yet</div>
                <p className="text-xs text-neutral-500 mt-1">Add your first customer above.</p>
              </div>
            ) : (
              customers.map((c) => (
                <div key={c.id} data-testid={`cust-row-${c.id}`} className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 p-3">
                  <div className={`w-10 h-10 rounded-full grid place-items-center font-display ${c.source === "browser" ? "bg-[#E3DAC9] text-neutral-900" : "bg-[#FADADD] text-neutral-900"}`}>
                    {c.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {c.name}
                      {c.source === "browser" && (
                        <span className="text-[9px] tracking-widest uppercase text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded-full">
                          Visitor
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate">{c.whatsapp_number}{c.tag ? ` · ${c.tag}` : ""}</div>
                  </div>
                  <button data-testid={`cust-del-${c.id}`} onClick={() => del(c.id)} className="w-9 h-9 rounded-full border border-black/10 text-red-600 grid place-items-center press">
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </div>
              ))
            )}
          </div>

          <button
            data-testid="next-select-btn"
            onClick={() => setStep("select")}
            disabled={customers.length === 0}
            className="w-full h-12 rounded-full bg-neutral-900 text-white text-sm font-medium press disabled:opacity-40 flex items-center justify-center gap-2 mt-4"
          >
            Continue <ChevronRight size={16} />
          </button>
        </div>
      )}

      {step === "select" && (
        <div className="px-5 mt-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Optional intro line</label>
            <input
              data-testid="broadcast-intro"
              value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="e.g. New Diwali collection just arrived 🌸"
              className="mt-1 w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Selected ({selected.length})</div>
              <button data-testid="picker-open-btn" onClick={() => setPickerOpen(true)} className="text-xs underline underline-offset-4">
                {selected.length ? "Change" : "Pick products"}
              </button>
            </div>
            {selected.length === 0 ? (
              <button
                data-testid="picker-empty-btn"
                onClick={() => setPickerOpen(true)}
                className="w-full py-8 rounded-2xl border-2 border-dashed border-black/15 bg-zinc-50 text-sm text-neutral-500 press"
              >
                + Choose products to broadcast
              </button>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {selectedProducts.map((p) => (
                  <div key={p.id} className="relative rounded-xl overflow-hidden aspect-square bg-zinc-50">
                    {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                      <div className="text-[10px] text-white truncate">{p.name}</div>
                    </div>
                    <button
                      data-testid={`unpick-${p.id}`}
                      onClick={() => toggleProduct(p.id)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center press"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="build-message-btn"
            onClick={buildMessage}
            disabled={selected.length === 0}
            className="w-full h-12 rounded-full bg-neutral-900 text-white text-sm font-medium press disabled:opacity-40"
          >
            Build broadcast message
          </button>
        </div>
      )}

      {step === "send" && (
        <div className="px-5 mt-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Preview</div>
              <button data-testid="copy-msg-btn" onClick={copyMsg} className="text-xs flex items-center gap-1 text-neutral-700">
                <Copy size={12} /> Copy
              </button>
            </div>
            <textarea
              data-testid="broadcast-message"
              value={message} onChange={(e) => setMessage(e.target.value)}
              rows={10}
              className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-neutral-900 bg-white leading-relaxed"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Send to ({customers.length})</div>
            <p className="text-[11px] text-neutral-500 mb-3">Tap each customer to open WhatsApp with the message pre-filled. WhatsApp doesn't allow true bulk send from a personal number — this simulates a broadcast list ethically.</p>
            <div className="space-y-2">
              {customers.map((c) => (
                <a
                  key={c.id}
                  data-testid={`send-to-${c.id}`}
                  href={waLink(c.whatsapp_number)}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 p-3 press"
                >
                  <div className="w-9 h-9 rounded-full bg-[#25D366] grid place-items-center text-white">
                    <MessageCircle size={16} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-neutral-500 truncate">{c.whatsapp_number}</div>
                  </div>
                  <Send size={14} className="text-neutral-400" />
                </a>
              ))}
            </div>
          </div>
          <div className="h-8" />
        </div>
      )}

      {/* Product picker sheet */}
      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setPickerOpen(false)}
          >
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-xl">Pick products</div>
                <button data-testid="picker-close" onClick={() => setPickerOpen(false)} className="w-9 h-9 rounded-full border border-black/10 grid place-items-center press">
                  <X size={14} />
                </button>
              </div>
              {products.length === 0 ? (
                <p className="text-sm text-neutral-500 py-6 text-center">Add products first.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {products.map((p) => {
                    const active = selected.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        data-testid={`pick-${p.id}`}
                        onClick={() => toggleProduct(p.id)}
                        className={`relative rounded-2xl overflow-hidden border-2 press text-left ${active ? "border-neutral-900" : "border-transparent"}`}
                      >
                        <div className="aspect-[4/5] bg-zinc-50">
                          {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="p-2">
                          <div className="text-xs font-medium truncate">{p.name}</div>
                          <div className="text-[10px] text-neutral-500">₹{Math.round(p.selling_price)}</div>
                        </div>
                        {active && (
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-neutral-900 text-white grid place-items-center">
                            <Check size={12} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                data-testid="picker-done"
                onClick={() => setPickerOpen(false)}
                className="w-full h-12 rounded-full bg-neutral-900 text-white text-sm font-medium mt-4 press"
              >
                Done ({selected.length})
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppFrame>
  );
}

const StepDot = ({ active, label, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex-1 rounded-full px-3 py-1.5 press ${active ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-500"}`}
  >
    {label}
  </button>
);
